import React from 'react';
import { connect, PromiseState } from 'react-refetch';
import { useSelector, useDispatch } from 'react-redux';

import ListViewContainer from '../shared/ListViewContainer';
import Box from 'components/elements/Box';
import Button from 'components/elements/button';
import { ICustomList } from '../AddToList/shared';
import TableLoader from 'components/utility/ContentLoader/TableLoader/TableLoader';

import getCustomListUrl from 'url-constructors/getCustomListUrl';
import getCustomListInvitationURL from 'url-constructors/getCustomListInvitationURL';
import ModalCustomListInfo from './ModalCustomListInfo';
import { isUserCuratorForList } from '../utils/isUserCuratorForList';
import { selectUserId } from 'redux/reducers/authentication';
import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import { IInvitation } from '../ShareList/shared';
import { removeFromMyLists, isInMyLists } from 'redux/reducers/myLists';
import P from 'components/elements/p';

function datasources(props: IOuterProps): any {
  const { listId, invitationId, close } = props;
  return {
    customList: getCustomListUrl({
      listId: listId,
      expand: 'permissions',
    }),
    invitation: getCustomListInvitationURL({
      listId: listId,
      invitationId: invitationId,
    }),
    acceptInvitation: (): any => {
      return {
        acceptInvitationData: {
          url: getCustomListInvitationURL({
            listId,
            invitationId,
            isWithDrawal: true,
          }),
          method: 'POST',
          then: close,
        },
      };
    },
  };
}

interface IOuterProps {
  listId: string;
  invitationId: string;
  close: () => void;
}

interface IAcceptInviteProps extends IOuterProps {
  customList: PromiseState<ICustomList>;
  invitation: PromiseState<IInvitation>;
  acceptInvitationData: PromiseState<any>;
  acceptInvitation: () => any;
}

const AcceptInvite: React.FC<IAcceptInviteProps> = (props) => {
  const {
    customList = PromiseState.create(),
    invitation = PromiseState.create(),
    acceptInvitationData = PromiseState.create(),
    acceptInvitation,
    close,
  } = props;

  const dispatch = useDispatch();

  const [, pending] = useCombinedRefetchState([customList, invitation]);

  const userId = useSelector(selectUserId);
  const isCurator = isUserCuratorForList({
    customList: customList.value,
    userId,
  });

  // If the TTL (time to live in seconds) is smaller than 1, it means the invitation has expired
  const isExpired = invitation.value?.ttl < 1;
  const isInvalid = invitation.rejected;

  const showInvite =
    !pending &&
    customList.settled &&
    invitation.settled &&
    !isCurator &&
    !isExpired &&
    !isInvalid;
  const showCuratorMessage = !pending && isCurator && !isExpired && !isInvalid;

  const listHasAlreadyBeenAdded = useSelector(
    isInMyLists(customList.value?.id)
  );

  const accept = (): void => {
    if (listHasAlreadyBeenAdded) {
      // Bit of a dirty cast to circumvent redux's nebulous typing
      const result = (dispatch(
        removeFromMyLists([customList.value.id])
      ) as any) as Promise<any>;
      result.then(() => {
        acceptInvitation();
      });
    } else {
      acceptInvitation();
    }
  };

  return (
    <ListViewContainer
      title="Invitation to curate"
      close={close}
      rejectedMessage={
        acceptInvitationData.rejected
          ? 'Something went wrong, please try again'
          : undefined
      }
    >
      <Box>
        {pending && (
          <table>
            <tbody>
              <TableLoader columnCount={1} />
            </tbody>
          </table>
        )}
        {showInvite && (
          <>
            <P pb={2}>
              A list curator has invited you to co-curate this list: By
              accepting this invite you&#39;ll be able to add or remove items
              from the list - as well as change the ordering.
            </P>
            <P pb={4}>
              Should you wish to leave the list or stop curating it you can
              still do so at any time.
            </P>
            <ModalCustomListInfo customList={customList.value} />
            <P pt={4}>
              By accepting this invite you gain curator access to edit this
              list.
            </P>
          </>
        )}
        {showCuratorMessage && (
          <P>
            You already are a curator for this list and do not need to accept
            this invitation again.
          </P>
        )}
        {isExpired && (
          <>
            <P pb={2}>Unfortunately this invitation has expired.</P>
            <P>
              If you would still like to be a curator for this list, please ask
              the sender of the invitation to send you a new one.
            </P>
          </>
        )}
        {isInvalid && (
          <P>
            Unfortunately this invitation is invalid. Please ask the sender to
            send you a new invitation.
          </P>
        )}
      </Box>
      <Box
        mt={'1em'}
        display="flex"
        justifyContent="flex-end"
        alignContent="stretch"
        alignItems="flex-end"
        mb={'1em'}
      >
        {showInvite && (
          <>
            <Button
              variant="secondary"
              alignSelf="auto"
              fontWeight="600"
              marginRight={'.5em'}
              onClick={close}
            >
              Discard invite
            </Button>
            <Button
              variant="primary"
              alignSelf="auto"
              fontWeight="600"
              onClick={accept}
            >
              Accept invite
            </Button>
          </>
        )}
        {(showCuratorMessage || isExpired || isInvalid) && (
          <Button
            variant="primary"
            onClick={close}
            alignSelf="auto"
            fontWeight="600"
          >
            Close
          </Button>
        )}
      </Box>
    </ListViewContainer>
  );
};

export default connect<IOuterProps>(datasources)(AcceptInvite as any);
